/**
 * Refazer a tabela de fusíveis a partir do pinout, e importar de planilha.
 *
 * As duas operações partilham a mesma forma — **confere primeiro, aplica
 * depois** — e a mesma razão para ela existir: as duas apagam ou acrescentam em
 * massa, e o sistema antigo faz as duas sem mostrar nada antes.
 *
 * **A regeneração é a que mais assusta, e com motivo.** Ela apaga todas as
 * funções da caixa e monta de novo do pinout. A regra de fusível só aceita pino
 * com "F" no nome, e boa parte das caixas tem funções de pinos que não têm: a
 * caixa 142 tem 127 gravadas e a regra produz 19. Então a tela mostra a conta —
 * quantas saem, quantas entram, quantas se perdem — antes de haver botão de
 * aplicar. No sistema antigo o botão chama-se "Restaurar" e não mostra número
 * nenhum.
 *
 * **Caixa em modo manual não regenera nada.** O legado apaga as funções sempre
 * e só refaz quando o modo é 1; nas caixas digitadas à mão ele esvazia e não
 * repõe. A API recusa, e aqui isso vira uma segunda porta com o aviso escrito.
 */

import { useRef, useState } from "react"
import {
  usarImportarFusiveis, usarRegenerarCaixa,
  type ConferenciaDeFusiveis, type Regeneracao,
} from "@/api/caixasFusiveis"
import { Aviso, Botao, Campo, Secao, estiloCampo, mensagem } from "../outros/pecas"

export function RegenerarCaixa({ projetoId, caixaId, titulo }: {
  projetoId: number
  caixaId: number
  titulo: string
}) {
  const regenerar = usarRegenerarCaixa(projetoId, caixaId)
  const [previa, setPrevia] = useState<Regeneracao | null>(null)
  const [recusa, setRecusa] = useState<string | null>(null)

  function conferir(forcarModoManual = false) {
    setRecusa(null)
    regenerar.mutate(
      { aplicar: false, forcarModoManual },
      { onSuccess: setPrevia, onError: (e) => { setPrevia(null); setRecusa(mensagem(e)) } },
    )
  }

  function aplicar() {
    regenerar.mutate(
      { aplicar: true, forcarModoManual: previa?.modo !== 1 },
      { onSuccess: setPrevia, onError: (e) => setRecusa(mensagem(e)) },
    )
  }

  const perde = previa ? previa.funcoesQueSaem - previa.funcoesQueEntram : 0

  return (
    <Secao titulo="Refazer a partir do pinout">
      <p style={{ margin: "0 0 8px", fontSize: 11.5, color: "var(--txt2)", maxWidth: "76ch", lineHeight: 1.55 }}>
        Apaga <strong>todas</strong> as funções de <em>{titulo}</em> e monta de novo a partir dos
        pinos do componente, restritos aos modelos da caixa. É o "Restaurar" do sistema antigo,
        que faz isso sem mostrar número nenhum.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <Botao aoClicar={() => conferir()} ocupado={regenerar.isPending}>
          {regenerar.isPending ? "Conferindo…" : "Conferir o que aconteceria"}
        </Botao>
      </div>

      {recusa && (
        <>
          <Aviso>{recusa}</Aviso>
          {recusa.includes("modo manual") && (
            <Botao aoClicar={() => conferir(true)}>
              Conferir mesmo assim (modo manual)
            </Botao>
          )}
        </>
      )}

      {previa && (
        <div style={{ border: "1px solid var(--line2)", borderRadius: 8, padding: 12, background: "var(--surface)" }}>
          {previa.aplicado && (
            <p style={{ margin: "0 0 8px", fontSize: 12.5, color: "var(--ok)" }}>
              Aplicado: {previa.funcoesQueEntram} função(ões) em {previa.tabelasQueSeriamCriadas} tabela(s).
            </p>
          )}

          <div style={{ display: "flex", gap: 22, marginBottom: 10, flexWrap: "wrap" }}>
            <Conta valor={previa.funcoesQueSaem} rotulo="funções saem" />
            <Conta valor={previa.funcoesQueEntram} rotulo="funções entram" />
            <Conta
              valor={perde}
              rotulo="se perdem"
              alerta={perde > 0}
              dica="A diferença não volta: são funções de pinos que a regra de fusível não reconhece."
            />
            <Conta valor={previa.pinosLidos} rotulo="pinos lidos" />
            <Conta valor={previa.pinosAproveitados} rotulo="viraram fusível" />
          </div>

          {previa.avisos.map((a, i) => <Aviso key={i}>{a}</Aviso>)}

          {previa.amostra.length > 0 && (
            <>
              <p style={{ margin: "10px 0 4px", fontFamily: "var(--fm)", fontSize: 10.5,
                          letterSpacing: ".08em", textTransform: "uppercase", color: "var(--txt3)" }}>
                Amostra do que entraria
              </p>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
                <thead>
                  <tr>
                    {["Pino no pinout", "Vira a tabela", "Conector", "Função", "Circuito"].map((c) => (
                      <th key={c} style={{ textAlign: "left", padding: "3px 6px", fontSize: 10.5,
                                           color: "var(--txt2)", borderBottom: "1px solid var(--line)" }}>
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previa.amostra.slice(0, 12).map((a) => (
                    <tr key={a.pinoutId}>
                      <td className="mono" style={celula}>{a.pinoBruto}</td>
                      <td className="mono" style={celula}>{a.nome}</td>
                      <td className="mono" style={celula}>{a.conector}</td>
                      <td style={celula}>{a.funcao || "—"}</td>
                      <td style={celula}>{a.circuito || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {!previa.aplicado && (
            <div style={{ marginTop: 12 }}>
              <Botao aoClicar={aplicar} ocupado={regenerar.isPending}>
                {perde > 0
                  ? `Aplicar mesmo perdendo ${perde} função(ões)`
                  : "Aplicar"}
              </Botao>
            </div>
          )}
        </div>
      )}
    </Secao>
  )
}

// ------------------------------------------------------------------ planilha

export function ImportarFusiveis({ projetoId, caixaId, titulo }: {
  projetoId: number
  caixaId: number | null
  titulo: string
}) {
  const importar = usarImportarFusiveis(projetoId)
  const campo = useRef<HTMLInputElement>(null)
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [colunaCaixa, setColunaCaixa] = useState(1)
  const [colunaNome, setColunaNome] = useState(caixaId ? 1 : 2)
  const [colunaDescricao, setColunaDescricao] = useState(caixaId ? 2 : 3)
  const [relatorio, setRelatorio] = useState<ConferenciaDeFusiveis | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  function enviar(aplicar: boolean) {
    if (!arquivo) return
    setErro(null)
    importar.mutate(
      { arquivo, caixaId, colunaCaixa, colunaNome, colunaDescricao, aplicar },
      { onSuccess: setRelatorio, onError: (e) => { setRelatorio(null); setErro(mensagem(e)) } },
    )
  }

  return (
    <Secao titulo={caixaId ? "Importar fusíveis de planilha" : "Importar fusíveis do projeto"}>
      <p style={{ margin: "0 0 8px", fontSize: 11.5, color: "var(--txt2)", maxWidth: "76ch", lineHeight: 1.55 }}>
        {caixaId
          ? <>Cria tabelas de fusível em <em>{titulo}</em> a partir de uma planilha de duas colunas.</>
          : <>Cria tabelas de fusível em várias caixas do projeto — a planilha traz o id da caixa
             numa terceira coluna. Caixa de outro projeto vira linha recusada, não gravação
             escondida.</>}
        {" "}A linha 1 é cabeçalho. <strong>O que já existe não entra de novo</strong>: o sistema
        antigo insere sempre, e importar duas vezes cria a mesma tabela duas vezes.
      </p>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Campo rotulo="Planilha">
          <input
            ref={campo}
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => { setArquivo(e.target.files?.[0] ?? null); setRelatorio(null) }}
            style={{ ...estiloCampo, padding: 4 }}
          />
        </Campo>
        {!caixaId && (
          <Campo rotulo="Coluna do id da caixa">
            <input type="number" min={1} value={colunaCaixa}
                   onChange={(e) => setColunaCaixa(Number(e.target.value))} style={estiloCampo} />
          </Campo>
        )}
        <Campo rotulo="Coluna do nome">
          <input type="number" min={1} value={colunaNome}
                 onChange={(e) => setColunaNome(Number(e.target.value))} style={estiloCampo} />
        </Campo>
        <Campo rotulo="Coluna da descrição">
          <input type="number" min={1} value={colunaDescricao}
                 onChange={(e) => setColunaDescricao(Number(e.target.value))} style={estiloCampo} />
        </Campo>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <Botao aoClicar={() => enviar(false)} desligado={!arquivo} ocupado={importar.isPending}>
          {importar.isPending ? "Lendo…" : "Conferir a planilha"}
        </Botao>
        {relatorio && !relatorio.aplicado && relatorio.vaoEntrar > 0 && (
          <Botao destaque aoClicar={() => enviar(true)} ocupado={importar.isPending}>
            Aplicar as {relatorio.vaoEntrar} linha(s)
          </Botao>
        )}
      </div>

      {erro && <Aviso>{erro}</Aviso>}

      {relatorio && (
        <div style={{ border: "1px solid var(--line2)", borderRadius: 8, padding: 12, background: "var(--surface)" }}>
          {relatorio.aplicado && (
            <p style={{ margin: "0 0 8px", fontSize: 12.5, color: "var(--ok)" }}>
              Aplicado: {relatorio.criados} tabela(s) criada(s).
            </p>
          )}

          <div style={{ display: "flex", gap: 22, marginBottom: 10, flexWrap: "wrap" }}>
            <Conta valor={relatorio.linhasNaPlanilha} rotulo="linhas" />
            <Conta valor={relatorio.vaoEntrar} rotulo="vão entrar" />
            <Conta valor={relatorio.jaExistem} rotulo="já existem" />
            <Conta valor={relatorio.foraDoProjeto} rotulo="de outro projeto" alerta={relatorio.foraDoProjeto > 0} />
            <Conta valor={relatorio.caixaNaoExiste} rotulo="caixa não existe" alerta={relatorio.caixaNaoExiste > 0} />
            <Conta valor={relatorio.semDados} rotulo="incompletas" />
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
            <thead>
              <tr>
                {["Linha", "Caixa", "Fusível", "Descrição", "O que acontece"].map((c) => (
                  <th key={c} style={{ textAlign: "left", padding: "3px 6px", fontSize: 10.5,
                                       color: "var(--txt2)", borderBottom: "1px solid var(--line)" }}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {relatorio.linhas.slice(0, 40).map((l) => (
                <tr key={l.linha}>
                  <td className="mono" style={celula}>{l.linha}</td>
                  <td className="mono" style={celula}>{l.caixaId || "—"}</td>
                  <td className="mono" style={celula}>{l.nomeCiclo || "—"}</td>
                  <td style={celula}>{l.descricao || "—"}</td>
                  <td style={{ ...celula, color: l.situacao === "vai entrar" ? "var(--ok)" : "var(--txt2)" }}>
                    {l.situacao}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {relatorio.linhas.length > 40 && (
            <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--txt3)" }}>
              Mostrando 40 de {relatorio.linhas.length} linhas.
            </p>
          )}
        </div>
      )}
    </Secao>
  )
}

const celula: React.CSSProperties = {
  padding: "3px 6px", borderBottom: "1px solid var(--line2)", verticalAlign: "top",
}

function Conta({ valor, rotulo, alerta, dica }: {
  valor: number
  rotulo: string
  alerta?: boolean
  dica?: string
}) {
  return (
    <span title={dica} style={{ display: "flex", flexDirection: "column", cursor: dica ? "help" : undefined }}>
      <strong style={{
        fontFamily: "var(--fm)", fontSize: 17, fontVariantNumeric: "tabular-nums",
        color: alerta && valor > 0 ? "var(--erro)" : undefined,
      }}>
        {valor.toLocaleString("pt-BR")}
      </strong>
      <span style={{ fontSize: 11, color: "var(--txt3)" }}>{rotulo}</span>
    </span>
  )
}
